import { SelectOption } from '@shape-ds/components';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  MODELS_BY_ASSET,
  NOTIFICATION_DELETE,
  NOTIFICATION_UPDATE,
  NOTIFICATIONS_CREATE,
  NOTIFICATIONS_LIST,
} from 'config/endpoints';
import { get, post, put, del } from 'utils/axios';
import { ModelByAsset, NotificationItem, NotificationsList } from './contract';

export const useNotificationsListQuery = () =>
  useQuery<NotificationsList>(
    ['NOTIFICATIONS', 'NOTIFICATIONS_LIST'],
    get<NotificationsList>(NOTIFICATIONS_LIST),
  );

export const useModelsByAssetQuery = (
  assetId?: string,
  queryOptions?: Omit<
    UseQueryOptions<ModelByAsset, unknown, SelectOption[]>,
    'queryKey' | 'queryFn' | 'select'
  >,
) =>
  useQuery<ModelByAsset, unknown, SelectOption[]>(
    ['MODELS_BY_ASSET', assetId],
    get<ModelByAsset>(MODELS_BY_ASSET(assetId)),
    {
      select: (data: ModelByAsset): SelectOption[] => {
        return data.map((model) => ({
          label: model.label,
          value: model.id,
        }));
      },
      enabled: !!assetId,
      ...queryOptions,
    },
  );
export const useEditNotificationMutation = (
  onSuccess?: (data: void, variables: NotificationItem) => void,
  onError?: () => void,
) => {
  const queryClient = useQueryClient();
  return useMutation(
    ['EDIT_NOTIFICATIONS'],
    async (data: NotificationItem) => {
      await put(NOTIFICATION_UPDATE(data.id), {
        payload: { model_id: data.model_id, frequency: data.frequency },
      });
    },
    {
      onSuccess: (data, variables) => {
        queryClient.refetchQueries({ queryKey: ['NOTIFICATIONS', 'NOTIFICATIONS_LIST'] });
        if (onSuccess) {
          onSuccess(data, variables);
        }
      },
      onError,
    },
  );
};
export const useAddNotificationMutation = (
  onSuccess?: (data: void, variables: NotificationItem) => void,
  onError?: () => void,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, NotificationItem>(
    ['ADD_NOTIFICATION'],
    async (data: NotificationItem) => {
      await post(NOTIFICATIONS_CREATE, {
        payload: { model_id: data.model_id, frequency: data.frequency },
      });
    },
    {
      onSuccess: (data, variables) => {
        queryClient.refetchQueries({ queryKey: ['NOTIFICATIONS', 'NOTIFICATIONS_LIST'] });
        if (onSuccess) {
          onSuccess(data, variables);
        }
      },
      onError,
    },
  );
};
export const useDeleteNotificationMutation = (
  onSuccess?: (data: void, variables: string) => void,
  onError?: () => void,
) => {
  const queryClient = useQueryClient();
  return useMutation(
    ['DELETE_NOTIFICATION'],
    async (id: string) => {
      await del(NOTIFICATION_DELETE(id), { params: { config_id: id } });
    },
    {
      onSuccess: (data, variables) => {
        queryClient.refetchQueries({ queryKey: ['NOTIFICATIONS', 'NOTIFICATIONS_LIST'] });
        if (onSuccess) {
          onSuccess(data, variables);
        }
      },
      onError,
    },
  );
};
